/**
 * Animated lit-diya centerpiece (orbital rings + mandala + flame + embers).
 * Shared by the home PoojaEnergizationSection and the /pooja-energization page
 * hero — meant to sit on a dark background.
 */
export default function DiyaVisual() {
  return (
    <div className="relative h-125 flex items-center justify-center overflow-hidden">
      {/* Concentric orbital rings */}
      <div className="absolute w-107.5 h-107.5 rounded-full border border-goldPrimary/6"></div>
      <div className="absolute w-82.5 h-82.5 rounded-full border border-goldPrimary/10 animate-[spin_70s_linear_infinite]">
        <span className="absolute -top-0.75 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-goldPrimary/60 shadow-[0_0_8px_2px_rgba(242,160,7,0.5)]"></span>
      </div>
      <div className="absolute w-57.5 h-57.5 rounded-full border border-dashed border-secondary/15 animate-[spin_45s_linear_infinite_reverse]"></div>

      {/* Warm ambient glow behind the flame */}
      <div className="absolute w-52 h-52 rounded-full bg-[radial-gradient(circle,rgba(242,160,7,0.28)_0%,transparent_70%)] blur-2xl animate-pulse" style={{ animationDuration: '3.5s' }}></div>

      {/* Decorative rangoli / mandala */}
      <svg viewBox="0 0 400 400" className="absolute w-120 h-120 opacity-[0.13] animate-[spin_120s_linear_infinite]" fill="none" stroke="#F2A007" aria-hidden="true">
        <circle cx="200" cy="200" r="196" strokeWidth="1" />
        <circle cx="200" cy="200" r="168" strokeWidth="0.6" strokeDasharray="1 5" />
        <circle cx="200" cy="200" r="96" strokeWidth="0.6" strokeDasharray="2 4" />
        {Array.from({ length: 16 }).map((_, i) => (
          <path key={`o${i}`} d="M200 22 Q218 78 200 132 Q182 78 200 22 Z" transform={`rotate(${i * 22.5} 200 200)`} strokeWidth="0.8" />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <path key={`i${i}`} d="M200 108 Q214 150 200 190 Q186 150 200 108 Z" transform={`rotate(${i * 45} 200 200)`} strokeWidth="0.7" />
        ))}
      </svg>

      {/* Diya */}
      <svg viewBox="0 0 240 240" className="relative z-10 w-64 h-64 select-none" role="img" aria-label="Lit diya">
        <defs>
          <radialGradient id="diyaGlow" cx="50%" cy="44%" r="42%">
            <stop offset="0%" stopColor="#FFE7A6" stopOpacity="0.7" />
            <stop offset="55%" stopColor="#F2A007" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#F2A007" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="flameGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFF6D6" />
            <stop offset="45%" stopColor="#FFC04A" />
            <stop offset="100%" stopColor="#E8770F" />
          </linearGradient>
          <linearGradient id="bowlGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F6D68A" />
            <stop offset="45%" stopColor="#CF9528" />
            <stop offset="100%" stopColor="#6E4210" />
          </linearGradient>
          <linearGradient id="bowlRim" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#8A5E1E" />
            <stop offset="50%" stopColor="#FBEBB4" />
            <stop offset="100%" stopColor="#8A5E1E" />
          </linearGradient>
          <filter id="flameBlur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.2" />
          </filter>
        </defs>

        {/* ambient */}
        <circle cx="120" cy="112" r="98" fill="url(#diyaGlow)" />

        {/* flame */}
        <g className="diya-flame">
          <path d="M120 58 C100 92 98 120 120 152 C142 120 140 92 120 58 Z" fill="url(#flameGrad)" filter="url(#flameBlur)" opacity="0.95" />
          <path d="M120 84 C109 104 109 126 120 150 C131 126 131 104 120 84 Z" fill="#FFF7DC" />
          <ellipse cx="120" cy="140" rx="7" ry="12" fill="#F2A007" opacity="0.5" />
        </g>

        {/* wick */}
        <rect x="117" y="146" width="6" height="10" rx="3" fill="#3F2408" />

        {/* lamp */}
        <ellipse cx="120" cy="156" rx="72" ry="12" fill="url(#bowlRim)" />
        <path d="M50 156 Q120 212 190 156 Q120 176 50 156 Z" fill="url(#bowlGrad)" />
        <path d="M66 160 Q120 196 174 160 Q120 178 66 160 Z" fill="#ffffff" opacity="0.09" />
        <ellipse cx="120" cy="198" rx="30" ry="7" fill="#3F2408" opacity="0.55" />
      </svg>

      {/* Gentle reflection beneath the lamp */}
      <div className="absolute left-1/2 top-1/2 z-8 -translate-x-1/2 translate-y-19.5 pointer-events-none">
        <div className="h-24 w-48 rounded-[50%] bg-[radial-gradient(ellipse_at_top,rgba(242,160,7,0.22)_0%,transparent_68%)] blur-md"></div>
        <div className="mx-auto -mt-25 h-px w-32 bg-linear-to-r from-transparent via-goldPrimary/40 to-transparent"></div>
      </div>

      {/* Rising embers */}
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className="ember-rise absolute h-1 w-1 rounded-full bg-goldPrimary/70 shadow-[0_0_6px_1px_rgba(242,160,7,0.6)]"
          style={{ left: `${46 + i * 3}%`, top: '46%', '--dur': `${5 + i}s`, '--delay': `${i * 1.1}s` }}
        ></span>
      ))}
    </div>
  )
}
